import { LOGISTICS_REPORTS_ROUTE, SALESFORCE_REPORTS_ROUTE } from "../constants/resources";
import type { SalesforceReportDashboardQuery } from "../generated/graphql";
import {
  SalesforceReportAggregate,
  SalesforceReportComponentKey,
  SalesforceReportDashboard,
  SalesforceReportGranularity,
} from "../generated/graphql";
import {
  BLANK_CATEGORY_KEY,
  BLANK_CATEGORY_LABEL,
  formatChartValue,
  formatCount,
  formatPeriodLong,
  formatPeriodTick,
  paletteColor,
} from "./salesforceDashboard";

export type ReportComponent = SalesforceReportDashboardQuery["salesforceReportDashboard"]["components"][number];

// What a chart is drawn from: a dashboard component, or the chart of a report page.
export type ReportChartData = Pick<
  ReportComponent,
  "chart" | "groupings" | "measures" | "points" | "sortByValue" | "showPercentages" | "source"
> &
  Partial<Pick<ReportComponent, "showValues" | "sortLegendValues">>;

export type ReportContext = "general" | "logistics";

type ReportDashboardTab = { slug: string; dashboard: SalesforceReportDashboard };

export const SALESFORCE_REPORT_TABS: readonly ReportDashboardTab[] = [
  { slug: "customer-success-2026", dashboard: SalesforceReportDashboard.CustomerSuccess_2026 },
  { slug: "sales-dashboard-ry", dashboard: SalesforceReportDashboard.SalesDashboardRy },
  { slug: "weekly-sales-sf2020", dashboard: SalesforceReportDashboard.WeeklySalesSf2020 },
  { slug: "care-documentation-systems", dashboard: SalesforceReportDashboard.CareDocumentationSystems },
  { slug: "support-metrics-monthly", dashboard: SalesforceReportDashboard.SupportMetricsMonthly },
  { slug: "churn-support", dashboard: SalesforceReportDashboard.ChurnSupport },
];

export const salesforceReportPath = (slug: string): string => `${SALESFORCE_REPORTS_ROUTE}/${slug}`;

export const SALESFORCE_REPORTS_DEFAULT_PATH = salesforceReportPath(SALESFORCE_REPORT_TABS[0].slug);

export const findReportTab = (slug: string | undefined): ReportDashboardTab | undefined =>
  SALESFORCE_REPORT_TABS.find(tab => tab.slug === slug);

export const salesforceReportDetailPath = (reportId: string, context: ReportContext = "general"): string =>
  `${context === "logistics" ? LOGISTICS_REPORTS_ROUTE : `${SALESFORCE_REPORTS_ROUTE}/report`}/${encodeURIComponent(reportId)}`;

// A detail cell's value that the report is narrowed to, as the column's equals filter.
export type SalesforceReportColumnFilter = { field: string; value: string };

// What a click on a dashboard component carries to its report page: the component, the clicked groupings' values
// (outermost first, null for the empty value) and the dashboard to go back to; and the cells' values the report is
// narrowed to.
export type SalesforceReportLink = {
  component?: SalesforceReportComponentKey;
  values?: ReadonlyArray<string | null>;
  dashboard?: string;
  columnFilters?: ReadonlyArray<SalesforceReportColumnFilter>;
};

const COMPONENT_PARAM = "component";
const VALUE_PARAM = "value";
const DASHBOARD_PARAM = "dashboard";
// A column filter is by.<field>=<value>.
const COLUMN_FILTER_PREFIX = "by.";

// An empty value= stands for the empty value.
export const salesforceReportUrl = (
  reportId: string,
  link: SalesforceReportLink = {},
  context: ReportContext = "general"
): string => {
  const params = new URLSearchParams();
  if (link.component) {
    params.set(COMPONENT_PARAM, link.component);
    (link.values ?? []).forEach(value => params.append(VALUE_PARAM, value ?? ""));
  }
  if (link.dashboard) params.set(DASHBOARD_PARAM, link.dashboard);
  (link.columnFilters ?? []).forEach(({ field, value }) => params.append(`${COLUMN_FILTER_PREFIX}${field}`, value));
  const query = params.toString();
  return query === ""
    ? salesforceReportDetailPath(reportId, context)
    : `${salesforceReportDetailPath(reportId, context)}?${query}`;
};

// The backend refuses an unknown report, a component of another report, clicked values a grouping cannot have and
// column filters the report cannot take with a CustomError, whose name and info never reach the client: PostGraphile
// sends its message only (the extended error fields are Postgres ones). These are those messages; any other error
// is a failed load.
const REPORT_REQUEST_ERROR_PATTERNS: readonly RegExp[] = [
  /^Unknown report /,
  / does not copy report /,
  /^More clicked values than groupings$/,
  /^Not a (?:day|number): /,
  / is not a column or grouping of report .* that can narrow it$/,
  /^Column .* filters report .* (?:twice|by an empty value)$/,
  /^Column filters require a selected chart block for a joined report$/,
];

export const isReportRequestError = (error: { graphQLErrors?: ReadonlyArray<{ message: string }> }): boolean =>
  (error.graphQLErrors ?? []).some(({ message }) =>
    REPORT_REQUEST_ERROR_PATTERNS.some(pattern => pattern.test(message))
  );

export type ParsedSalesforceReportLink = {
  component: SalesforceReportComponentKey | null;
  values: Array<string | null>;
  dashboard: string | null;
  columnFilters: SalesforceReportColumnFilter[];
};

const isComponentKey = (value: string | null): value is SalesforceReportComponentKey =>
  Object.values<string>(SalesforceReportComponentKey).includes(value ?? "");

// A hand-edited link may repeat a column filter or leave its value empty, which the server refuses: the first of
// each field counts, and a filter without a value is left out.
const readColumnFilters = (params: URLSearchParams): SalesforceReportColumnFilter[] => {
  const seen = new Set<string>();
  return Array.from(params.entries()).flatMap(([name, value]): SalesforceReportColumnFilter[] => {
    const field = name.startsWith(COLUMN_FILTER_PREFIX) ? name.slice(COLUMN_FILTER_PREFIX.length) : "";
    if (field === "" || seen.has(field)) return [];
    seen.add(field);
    return value === "" ? [] : [{ field, value }];
  });
};

// Values without a known component are dropped: they filter nothing.
export const parseSalesforceReportSearch = (search: string): ParsedSalesforceReportLink => {
  const params = new URLSearchParams(search);
  const component = params.get(COMPONENT_PARAM);
  return {
    component: isComponentKey(component) ? component : null,
    values: isComponentKey(component) ? params.getAll(VALUE_PARAM).map(value => (value === "" ? null : value)) : [],
    dashboard: params.get(DASHBOARD_PARAM) || null,
    columnFilters: readColumnFilters(params),
  };
};

// The address of the report that a link on a detail cell opens: the current one, narrowed to the cell's value
// besides its component, dashboard and other column filters (a filter on the same column is replaced).
export const salesforceReportFilterUrl = (
  reportId: string,
  current: ParsedSalesforceReportLink,
  field: string,
  value: string,
  context: ReportContext = "general"
): string =>
  salesforceReportUrl(
    reportId,
    {
      component: current.component ?? undefined,
      values: current.values,
      dashboard: current.dashboard ?? undefined,
      columnFilters: [...current.columnFilters.filter(filter => filter.field !== field), { field, value }],
    },
    context
  );

const ISO_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const YEAR_LENGTH = 4;

// A value of a grouping: null or undefined when the Salesforce field was empty.
type GroupValue = string | null | undefined;

const isBlank = (value: GroupValue): value is null | undefined => value === null || value === undefined || value === "";

// A date grouping arrives as its period's first day; the other values are the field's own text.
export const formatReportValue = (
  value: GroupValue,
  granularity: SalesforceReportGranularity | null | undefined
): string => {
  if (isBlank(value)) return BLANK_CATEGORY_LABEL;
  if (!granularity || !ISO_DAY_PATTERN.test(value)) return value;
  switch (granularity) {
    case SalesforceReportGranularity.Month:
      return formatPeriodLong(value, "month");
    case SalesforceReportGranularity.Year:
      return value.slice(0, YEAR_LENGTH);
    case SalesforceReportGranularity.Week:
    case SalesforceReportGranularity.Day:
      return formatPeriodLong(value, "day");
  }
};

const monthShortFormat = new Intl.DateTimeFormat("de-DE", { month: "short", year: "2-digit", timeZone: "UTC" });

// The same value in the short form of an axis under a narrow plot: a day or week as "05.01.", a month as "Jan 26".
export const formatReportValueShort = (
  value: GroupValue,
  granularity: SalesforceReportGranularity | null | undefined
): string => {
  if (isBlank(value) || !granularity || !ISO_DAY_PATTERN.test(value)) return formatReportValue(value, granularity);
  switch (granularity) {
    case SalesforceReportGranularity.Month:
      return monthShortFormat.format(new Date(`${value}T00:00:00Z`)).replace(".", "");
    case SalesforceReportGranularity.Year:
      return value.slice(0, YEAR_LENGTH);
    case SalesforceReportGranularity.Week:
    case SalesforceReportGranularity.Day:
      return formatPeriodTick(value, "day");
  }
};

export const formatMeasureValue = (aggregate: SalesforceReportAggregate, value: number | null): string => {
  if (value === null) return BLANK_CATEGORY_LABEL;
  return aggregate === SalesforceReportAggregate.Count || aggregate === SalesforceReportAggregate.Unique
    ? formatCount(value)
    : formatChartValue(value);
};

export type ReportSeries = { key: string; label: string; color: string };

export type ReportRow = {
  key: string;
  label: string;
  // The label in a form for a narrow axis; the label itself when missing.
  shortLabel?: string;
  // One value per series, in the series' order.
  values: Array<number | null>;
  // The per-group sum of the second measure, for a chart that draws a line over its columns.
  lineValue: number | null;
};

export type ReportBars = { series: ReportSeries[]; rows: ReportRow[] };

const addNullable = (a: number | null, b: number | null): number | null => {
  if (a === null) return b;
  if (b === null) return a;
  return a + b;
};

const rowTotal = (row: ReportRow): number => row.values.reduce<number>((total, value) => total + (value ?? 0), 0);

const groupKey = (value: GroupValue): string => (isBlank(value) ? BLANK_CATEGORY_KEY : value);

// The raw value a row or series key stands for; null for the empty value.
export const groupKeyValue = (key: string): string | null => (key === BLANK_CATEGORY_KEY ? null : key);

const distinct = (values: GroupValue[]): GroupValue[] => {
  const seen = new Set<string>();
  return values.filter(value => {
    const key = groupKey(value);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const sortedRows = (rows: ReportRow[], sortByValue: boolean): ReportRow[] =>
  sortByValue ? [...rows].sort((a, b) => rowTotal(b) - rowTotal(a)) : rows;

// One bar series per distinct second-grouping value, ordered and coloured by value.
export const buildSeriesBars = (component: ReportChartData): ReportBars => {
  const [firstGrouping, secondGrouping] = component.groupings;
  const { points } = component;
  // Salesforce lists the series as they first appear in the report's points, and colours them in that order.
  const seriesValues = distinct(points.map(point => point.series));
  if (component.sortLegendValues === true)
    seriesValues.sort((a, b) =>
      formatReportValue(a, secondGrouping?.granularity).localeCompare(
        formatReportValue(b, secondGrouping?.granularity),
        "de"
      )
    );
  const seriesIndex = new Map(seriesValues.map((value, index) => [groupKey(value), index]));
  const groups = distinct(points.map(point => point.group));
  const rows = new Map<string, ReportRow>(
    groups.map(group => [
      groupKey(group),
      {
        key: groupKey(group),
        label: formatReportValue(group, firstGrouping?.granularity),
        shortLabel: formatReportValueShort(group, firstGrouping?.granularity),
        values: seriesValues.map(() => null),
        lineValue: null,
      },
    ])
  );
  points.forEach(point => {
    const row = rows.get(groupKey(point.group));
    const index = seriesIndex.get(groupKey(point.series));
    if (!row || index === undefined) return;
    row.values[index] = addNullable(row.values[index], point.values[0] ?? null);
    row.lineValue = addNullable(row.lineValue, point.values[1] ?? null);
  });
  return {
    series: seriesValues.map((value, index) => ({
      key: groupKey(value),
      label: formatReportValue(value, secondGrouping?.granularity),
      color: paletteColor(index),
    })),
    rows: sortedRows(Array.from(rows.values()), component.sortByValue),
  };
};

// One bar per measure and group, side by side.
export const buildMeasureBars = (component: ReportChartData, measureLabels: string[]): ReportBars => {
  const [firstGrouping] = component.groupings;
  const groups = distinct(component.points.map(point => point.group));
  const rows = new Map<string, ReportRow>(
    groups.map(group => [
      groupKey(group),
      {
        key: groupKey(group),
        label: formatReportValue(group, firstGrouping?.granularity),
        shortLabel: formatReportValueShort(group, firstGrouping?.granularity),
        values: measureLabels.map(() => null),
        lineValue: null,
      },
    ])
  );
  component.points.forEach(point => {
    const row = rows.get(groupKey(point.group));
    if (!row) return;
    row.values = row.values.map((value, index) => addNullable(value, point.values[index] ?? null));
  });
  return {
    series: measureLabels.map((label, index) => ({ key: `measure-${index}`, label, color: paletteColor(index) })),
    rows: sortedRows(Array.from(rows.values()), component.sortByValue),
  };
};

// One bar per group of the first measure, without a second dimension.
export const buildGroupBars = (component: ReportChartData, seriesLabel: string): ReportBars =>
  buildMeasureBars({ ...component, measures: component.measures.slice(0, 1) }, [seriesLabel]);

export type ReportSlice = { label: string | null; count: number };

// Slices of the first measure per group; empty and negative values draw nothing.
export const buildDonutSlices = (component: ReportChartData): ReportSlice[] => {
  const { rows } = buildGroupBars(component, "");
  const [firstGrouping] = component.groupings;
  const groups = distinct(component.points.map(point => point.group));
  return groups.flatMap(group => {
    const count = rows.find(row => row.key === groupKey(group))?.values[0] ?? 0;
    return count > 0
      ? [{ label: isBlank(group) ? null : formatReportValue(group, firstGrouping?.granularity), count }]
      : [];
  });
};

export type SideBySideBar = {
  key: string;
  groupKey: string;
  groupLabel: string;
  groupShortLabel: string;
  seriesIndex: number;
  seriesLabel: string;
  color: string;
  value: number;
};

export type SideBySideGroup = { key: string; label: string; firstBar: number; barCount: number };

export type SideBySideBars = { bars: SideBySideBar[]; groups: SideBySideGroup[] };

// Salesforce draws a bar only for a point that has a value: the groups in order, each with its series in legend order.
export const buildSideBySideBars = ({ series, rows }: ReportBars): SideBySideBars => {
  const bars: SideBySideBar[] = [];
  const groups: SideBySideGroup[] = [];
  rows.forEach(row => {
    const firstBar = bars.length;
    series.forEach((entry, seriesIndex) => {
      const value = row.values[seriesIndex] ?? null;
      if (value === null) return;
      bars.push({
        key: `${row.key}::${entry.key}`,
        groupKey: row.key,
        groupLabel: row.label,
        groupShortLabel: row.shortLabel ?? row.label,
        seriesIndex,
        seriesLabel: entry.label,
        color: entry.color,
        value,
      });
    });
    if (bars.length > firstBar) {
      groups.push({ key: row.key, label: row.label, firstBar, barCount: bars.length - firstBar });
    }
  });
  return { bars, groups };
};

const TICK_INTERVALS = 4;
const TICK_STEPS = [1, 2, 5, 10];

// Round ticks from zero (or below it for a negative value) up to the first one at or above the largest value.
export const valueAxisTicks = (values: number[]): number[] => {
  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  const rough = (max - min || TICK_INTERVALS) / TICK_INTERVALS;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = (TICK_STEPS.find(candidate => candidate * magnitude >= rough) ?? 1) * magnitude;
  const first = Math.floor(min / step);
  const last = Math.max(Math.ceil(max / step), first + 1);
  return Array.from({ length: last - first + 1 }, (_, index) => Number(((first + index) * step).toPrecision(12)));
};

export const metricValue = (component: ReportChartData): number | null => component.points[0]?.values[0] ?? null;

const TICK_FONT_SIZE_PX = 12;
const TICK_CHAR_WIDTH_PX = 6.5;
const TICK_MARGIN_PX = 4;
const TICK_PROJECTION = Math.SQRT1_2;
export const MAX_TICK_LABEL_CHARS = 18;
export const MAX_SLANTED_TICK_HEIGHT_PX = 100;

const ELLIPSIS = "\u2026";

// A label longer than the maximum is cut to it, the last character being the ellipsis.
export const truncateLabel = (label: string, maxChars: number): string =>
  label.length > maxChars ? `${label.slice(0, maxChars - 1)}${ELLIPSIS}` : label;

export const truncateTickLabel = (label: string): string => truncateLabel(label, MAX_TICK_LABEL_CHARS);

// The height a -45 degree axis needs for the longest label it shows, at most the cap.
export const slantedTickHeight = (labels: string[]): number => {
  const longest = Math.max(0, ...labels.map(label => truncateTickLabel(label).length));
  const height = Math.ceil((longest * TICK_CHAR_WIDTH_PX + TICK_FONT_SIZE_PX) * TICK_PROJECTION) + TICK_MARGIN_PX;
  return Math.min(height, MAX_SLANTED_TICK_HEIGHT_PX);
};

// The width of a side-by-side bar that fills the available width, never under the minimum (the plot scrolls then)
// or over the maximum.
export const fitSideBySideBarWidth = ({
  available,
  barCount,
  groupCount,
  barGap,
  groupGap,
  minWidth,
  maxWidth,
}: {
  available: number;
  barCount: number;
  groupCount: number;
  barGap: number;
  groupGap: number;
  minWidth: number;
  maxWidth: number;
}): number => {
  if (barCount === 0) return minWidth;
  const fitted = (available - (barCount - groupCount) * barGap - groupCount * groupGap) / barCount;
  return Math.min(maxWidth, Math.max(minWidth, fitted));
};
